import { Injectable } from "@angular/core";
import { Actions, createEffect, ofType } from "@ngrx/effects";
import { select, Store } from "@ngrx/store";
import { ActionWithPayload, batchActions } from "@obg/ngrx-utils";
import { AppError, AUTH_ACTION_TYPE, MessageService } from "@obg/ngx.core";
import { PlatformService } from "@obg/ngx.global.evo";
import { interval, merge, of, timer } from "rxjs";
import { catchError, filter, map, pairwise, skipUntil, switchMap, takeUntil, withLatestFrom } from "rxjs/operators";

import { CommonState } from "../state";

import { OmsAction, omsActionType } from "./oms.action";
import { OmsConfigService } from "./oms.config";
import { omsMessageContext } from "./oms.const";
import {
	NotificationsInfo,
	NotificationsInfoRequest,
	OmsMessage,
	OmsMessageStatus,
	OmsService,
	OmsUserSettings,
	UpdateMessage,
	UpdateMessagePayload,
	UpdateMessagesPayload,
} from "./oms.model";
import { OmsSelector } from "./oms.selector";

@Injectable()
export class OmsEffect {
	/**
	 * @deprecated - [fabric-deprecated] - Use document.obgEventBus.retrieve(AccountAppValue.GetNotificationsByStatus) instead
	 */
	getNotificationsByStatus$ = createEffect(() =>
		this.actions$.pipe(
			ofType(omsActionType.GET_NOTIFICATIONS_BY_STATUS),
			switchMap((action: ActionWithPayload<OmsMessageStatus>) =>
				this.service.getNotificationsByStatus(action.payload).pipe(
					map((response: OmsMessage[]) =>
						this.action.getNotificationsByStatusSuccess({
							status: action.payload,
							notifications: response,
						})
					),
					catchError((error: AppError) => of(this.action.getNotificationsByStatusError(error)))
				)
			)
		)
	);

	/**
	 * @deprecated - [fabric-deprecated] - Use document.obgEventBus.retrieve(AccountAppValue.GetAlerts)
	 * OR
	 * document.obgEventBus.retrieve(AccountAppValue.PollAlerts) instead
	 */
	getAlerts$ = createEffect(() =>
		this.actions$.pipe(
			ofType(omsActionType.GET_ALERTS),
			withLatestFrom(this.store.pipe(select(this.omsSelector.getAlertsContext()))),
			switchMap(([, alertsContext]) =>
				this.service.getAlerts(alertsContext).pipe(
					map((response: OmsMessage[]) => this.action.getAlertsSuccess(response)),
					catchError((error: AppError) => of(this.action.getAlertsError(error)))
				)
			)
		)
	);

	/**
	 * @deprecated - [fabric-deprecated] - Use document.obgEventBus.retrieve(AccountAppValue.PollNotificationsInfo)
	 * OR
	 * document.obgEventBus.retrieve(AccountAppValue.GetNotificationsInfo) instead
	 */
	getNotificationsInfo$ = createEffect(() =>
		this.actions$.pipe(
			ofType(omsActionType.GET_NOTIFICATIONS_INFO),
			map((x: ActionWithPayload<NotificationsInfoRequest>) => x.payload),
			switchMap((notificationInfoParams: NotificationsInfoRequest) =>
				this.service.getNotificationsInfo(notificationInfoParams).pipe(
					map((response: NotificationsInfo) => this.action.getNotificationsInfoSuccess(response)),
					catchError((error: AppError) => of(this.action.getNotificationsInfoError(error)))
				)
			)
		)
	);

	/**
	 * @deprecated - [fabric-deprecated] - Use document.obgEventBus.dispatch(AccountAppCommand.UpdateMessage) instead
	 */
	updateMessage$ = createEffect(() =>
		this.actions$.pipe(
			ofType(omsActionType.UPDATE_MESSAGE),
			map((x: ActionWithPayload<UpdateMessagePayload>) => x.payload.message),
			switchMap((updateMessage: UpdateMessage) =>
				this.service.updateMessage(updateMessage).pipe(
					map(() =>
						batchActions(
							this.action.updateMessageSuccess(),
							this.action.getNotificationsInfo(this.configService.getNotificationInfoParams())
						)
					),
					catchError((error: AppError) => of(this.action.updateMessageError(error)))
				)
			)
		)
	);

	/**
	 * @deprecated - [fabric-deprecated] - Use document.obgEventBus.dispatch(AccountAppCommand.UpdateMessages) instead
	 */
	updateMessages$ = createEffect(() =>
		this.actions$.pipe(
			ofType(omsActionType.UPDATE_MESSAGES),
			map((x: ActionWithPayload<UpdateMessagesPayload>) => x.payload.messages),
			switchMap((updateMessages) =>
				this.service.updateMessages(updateMessages).pipe(
					map(() => this.action.updateMessagesSuccess()),
					catchError((error: AppError) => of(this.action.updateMessagesError(error)))
				)
			)
		)
	);

	/**
	 * @deprecated - [fabric-deprecated] - Use document.obgEventBus.retrieve(AccountAppValue.GetOmsUserSettings) instead
	 */
	getUserSettings$ = createEffect(() =>
		this.actions$.pipe(
			ofType(omsActionType.GET_USER_SETTINGS),
			switchMap(() =>
				this.service.getUserSettings().pipe(
					map((response: OmsUserSettings) => this.action.getUserSettingsSuccess(response)),
					catchError((error: AppError) => of(this.action.getUserSettingsError(error)))
				)
			)
		)
	);

	/**
	 * @deprecated - [fabric-deprecated] - Use document.obgEventBus.dispatch(AccountAppCommand.UpdateOmsUserSettings) instead
	 */
	updateUserSettings$ = createEffect(() =>
		this.actions$.pipe(
			ofType(omsActionType.UPDATE_USER_SETTINGS),
			map((x: ActionWithPayload<OmsUserSettings>) => x.payload),
			switchMap((userSettings: OmsUserSettings) =>
				this.service.updateUserSettings(userSettings).pipe(
					map((response: OmsUserSettings) => this.action.updateUserSettingsSuccess(response)),
					catchError((error: AppError) => of(this.action.updateUserSettingsError(error)))
				)
			)
		)
	);

	/**
	 * @deprecated - [fabric-deprecated] - Use document.obgEventBus.retrieve(AccountAppValue.PollAlerts) instead
	 */
	alertPollingStart$ = this.platformService.forClient(() =>
		createEffect(() =>
			this.actions$.pipe(
				ofType(omsActionType.ENABLE_REALTIME_ALERTS),
				switchMap(() =>
					interval(this.configService.getAlertPollingInterval()).pipe(
						withLatestFrom(this.store.pipe(select(this.omsSelector.getAlertsContext()))),
						switchMap(([, alertsContext]) =>
							this.service.getAlerts(alertsContext).pipe(
								map((response) => this.action.getAlertsSuccess(response)),
								catchError((error: AppError) => of(this.action.getAlertsError(error)))
							)
						),
						takeUntil(
							this.actions$.pipe(
								ofType(omsActionType.DISABLE_REALTIME_ALERTS, omsActionType.DISABLE_REALTIME_ALERTS)
							)
						)
					)
				)
			)
		)
	);

	/**
	 * @deprecated - [fabric-deprecated] - Use document.obgEventBus.retrieve(AccountAppValue.PollNotificationsInfo) instead
	 */
	notificationInfoPollingStart$ = this.platformService.forClient(() =>
		createEffect(() =>
			this.actions$.pipe(
				ofType(omsActionType.ENABLE_REALTIME_NOTIFICATIONS_INFO),
				switchMap(() =>
					timer(0, this.configService.getNotificationInfoPollingInterval()).pipe(
						switchMap(() =>
							this.service.getNotificationsInfo(this.configService.getNotificationInfoParams()).pipe(
								map((response) => this.action.getNotificationsInfoSuccess(response)),
								catchError((error: AppError) => of(this.action.getNotificationsInfoError(error)))
							)
						),
						takeUntil(this.actions$.pipe(ofType(omsActionType.DISABLE_REALTIME_NOTIFICATIONS_INFO)))
					)
				)
			)
		)
	);

	/**
	 * @deprecated - [fabric-deprecated]
	 */
	syncNotifications$ = createEffect(() =>
		this.actions$.pipe(
			ofType(AUTH_ACTION_TYPE.sessionStart),
			switchMap(() =>
				this.actions$.pipe(ofType(omsActionType.GET_NOTIFICATIONS_INFO_SUCCESS)).pipe(
					map((x: ActionWithPayload<NotificationsInfo>) => x.payload.unreadNotificationCount),
					pairwise(),
					filter(([prevCount, nextCount]) => prevCount !== nextCount),
					map(() => this.action.getNotificationsByStatus(OmsMessageStatus.Active)),
					skipUntil(
						this.actions$.pipe(
							ofType(omsActionType.GET_NOTIFICATIONS_BY_STATUS),
							filter(
								(action: ActionWithPayload<OmsMessageStatus>) =>
									action.payload === OmsMessageStatus.Active
							)
						)
					),
					takeUntil(this.actions$.pipe(ofType(AUTH_ACTION_TYPE.sessionEnd)))
				)
			)
		)
	);

	/**
	 * @deprecated - [fabric-deprecated]
	 */
	logoutSuccess$ = createEffect(() =>
		this.actions$.pipe(
			ofType(AUTH_ACTION_TYPE.sessionEnd),
			map(() => this.action.reset())
		)
	);

	/**
	 * @deprecated - [fabric-deprecated]
	 */
	initMessages$ = createEffect(() =>
		merge(
			this.messageService.mapErrorByContext$({
				[omsActionType.GET_NOTIFICATIONS_BY_STATUS_ERROR]: omsMessageContext.getNotificationsByStatus,
				[omsActionType.GET_ALERTS_ERROR]: omsMessageContext.getAlerts,
				[omsActionType.GET_NOTIFICATIONS_INFO_ERROR]: omsMessageContext.getNotificationsInfo,
				[omsActionType.UPDATE_MESSAGE_ERROR]: omsMessageContext.updateMessage,
				[omsActionType.UPDATE_MESSAGES_ERROR]: omsMessageContext.updateMessage,
				[omsActionType.GET_USER_SETTINGS_ERROR]: omsMessageContext.getUserSettings,
				[omsActionType.UPDATE_USER_SETTINGS_ERROR]: omsMessageContext.updateUserSettings,
			}),
			this.messageService.mapClearByContext$({
				[omsActionType.GET_NOTIFICATIONS_BY_STATUS]: omsMessageContext.getNotificationsByStatus,
				[omsActionType.GET_ALERTS]: omsMessageContext.getAlerts,
				[omsActionType.GET_NOTIFICATIONS_INFO]: omsMessageContext.getNotificationsInfo,
				[omsActionType.UPDATE_MESSAGE]: omsMessageContext.updateMessage,
				[omsActionType.UPDATE_MESSAGES]: omsMessageContext.updateMessage,
				[omsActionType.GET_USER_SETTINGS]: omsMessageContext.getUserSettings,
				[omsActionType.UPDATE_USER_SETTINGS]: omsMessageContext.updateUserSettings,
			})
		)
	);

	constructor(
		private actions$: Actions,
		private action: OmsAction,
		private service: OmsService,
		private configService: OmsConfigService,
		private store: Store<CommonState>,
		private omsSelector: OmsSelector,
		private messageService: MessageService,
		private platformService: PlatformService
	) {}
}
